import React, { useState, useEffect, useMemo, useRef } from "react";
import { getActivityKey } from "../../utils/checkout";
import { Box, MenuItem, Select, Typography, Checkbox, FormControlLabel } from "@mui/material";
import BpRadio from "./BpRadio";

const CmLabel = (props) => {
    const { children, ...lProps } = props;
    return (
        <label style={{ color: '#6D7280', fontSize: '14px' }} {...lProps}>{children}</label>
    );
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

/** ───────────────── Normalización de claves (IDs) ───────────────── **/
// Use shared getActivityKey to ensure the same normalization across the app
// Alias local name for clarity in this component
const norm = (v) => String(v ?? '');
const getAttractionKey = (a) => {
    if (a == null) return '';
    if (typeof a === 'string' || typeof a === 'number') return norm(a);
    return getActivityKey(a);
};

const Step2 = ({
    extrasGuide = [],
    extrasVehicle = [],
    extrasAttractions = [],
    initialGuide = null,
    persons = {},
    initialVehicle = null,
    initialAttractions = [],
    onSelectionChange = (..._args) => { },
    modalOpen = false,
    guideIncluded = undefined,
    initialVehicleOptionChecked = false
}) => {
    // ─────────── Guide options
    const hasGuideExtras = Array.isArray(extrasGuide) && extrasGuide.length > 0;
    const guideOptions = useMemo(() => {
        if (!hasGuideExtras) return [];
        return extrasGuide.map(g => {
            if (!g) return null;
            if (typeof g === 'string' || typeof g === 'number') {
                const id = norm(g);
                return { id, type_guide: id, price: 0 };
            }
            const stableId = g.id ?? g.type_guide ?? g.type ?? g.name ?? String(g.title ?? g.label ?? 'guide');
            return {
                id: norm(stableId),
                type_guide: g.type_guide ?? g.type ?? g.name ?? String(g.id ?? stableId),
                price: g.price ?? 0,
                excepcions: g.excepcions ?? []
            };
        }).filter(Boolean);
    }, [extrasGuide, hasGuideExtras]);

    // ─────────── Vehicle options
    const hasVehicleExtras = Array.isArray(extrasVehicle) && extrasVehicle.length > 0;
    const vehicleOptions = useMemo(() => {
        if (!hasVehicleExtras) return [];
        return extrasVehicle.map(v => {
            if (!v) return null;
            if (typeof v === 'string' || typeof v === 'number') {
                const id = norm(v);
                return { id, vehicle_type: id, price: 0 };
            }
            const stableId = v.id ?? v.vehicle_type ?? v.name ?? String(v.title ?? v.label ?? 'vehicle');
            return {
                id: norm(stableId),
                vehicle_type: v.vehicle_type ?? v.name ?? String(v.id ?? stableId),
                price: v.price ?? 0,
                vehicle_option: v.vehicle_option ?? null,
                excepcions: v.excepcions ?? []
            };
        }).filter(Boolean);
    }, [extrasVehicle, hasVehicleExtras]);

    // ─────────── State
    const [vehicleKey, setVehicleKey] = useState('');
    const [guideKey, setGuideKey] = useState('');
    const [vehicleOptionChecked, setVehicleOptionChecked] = useState(() => Boolean(initialVehicleOptionChecked));

    // selectedAttractions guarda SIEMPRE keys string
    const [selectedAttractions, setSelectedAttractions] = useState(() => {
        if (!Array.isArray(initialAttractions)) return [];
        return initialAttractions.map(a => getAttractionKey(a));
    });

    const attractionsInitRef = useRef(false);

    // ─────────── Handlers
    const handleChangeVehicle = (event) => setVehicleKey(event.target.value);
    const handleChangeGuide = (event) => setGuideKey(event.target.value);
    const handleToggleVehicleOption = () => setVehicleOptionChecked(prev => !prev);

    const toggleAttraction = (key) => {
        setSelectedAttractions(prev => {
            const exists = prev.includes(key);
            return exists ? prev.filter(n => n !== key) : [...prev, key];
        });
    };

    // ─────────── Init Guide
    useEffect(() => {
        if (!modalOpen) return;

        if (initialGuide && (typeof initialGuide === 'string' || typeof initialGuide === 'number' || initialGuide.id || initialGuide.type_guide)) {
            const id = typeof initialGuide === 'string' || typeof initialGuide === 'number'
                ? norm(initialGuide)
                : norm(initialGuide.id ?? initialGuide.type_guide);
            setGuideKey(id);
            return;
        }
        if (!hasGuideExtras) {
            setGuideKey('');
            return;
        }
        const freeGuide = guideOptions.find(g => Number(g.price) === 0) || guideOptions[0];
        setGuideKey(norm(freeGuide?.id ?? ''));
    }, [modalOpen, initialGuide, guideOptions, hasGuideExtras, guideIncluded]);

    // ─────────── Init Vehicle
    useEffect(() => {
        if (!modalOpen) return;

        if (vehicleOptions.length === 1 && String(vehicleOptions[0].id) === '0') {
            setVehicleKey('');
            return;
        }
        if (initialVehicle && (typeof initialVehicle === 'string' || typeof initialVehicle === 'number' || initialVehicle.id || initialVehicle.vehicle_type)) {
            const id = typeof initialVehicle === 'string' || typeof initialVehicle === 'number'
                ? norm(initialVehicle)
                : norm(initialVehicle.id ?? initialVehicle.vehicle_type);
            setVehicleKey(id);
            return;
        }
        if (!hasVehicleExtras) {
            setVehicleKey('');
            return;
        }
        const freeOption = vehicleOptions.find(v => Number(v.price) === 0) || vehicleOptions[0];
        if (freeOption) setVehicleKey(norm(freeOption.id));
    }, [modalOpen, initialVehicle, vehicleOptions, hasVehicleExtras]);

    // ─────────── Vehicle option checkbox initial
    useEffect(() => {
        const selectedVehicle = vehicleOptions.find(v => v.id === vehicleKey) || null;
        if (!selectedVehicle || !('vehicle_option' in selectedVehicle) || !selectedVehicle.vehicle_option) {
            setVehicleOptionChecked(Boolean(initialVehicleOptionChecked && (!selectedVehicle || !selectedVehicle.vehicle_option)) || false);
            return;
        }
        const vo = selectedVehicle.vehicle_option;
        const first = Array.isArray(vo) ? vo[0] : vo;
        const val = first?.value ?? 0;

        if (modalOpen && typeof initialVehicleOptionChecked !== 'undefined') {
            setVehicleOptionChecked(Boolean(initialVehicleOptionChecked));
        } else {
            setVehicleOptionChecked(Number(val) > 0);
        }
    }, [vehicleKey, vehicleOptions, modalOpen, initialVehicleOptionChecked]);

    // ─────────── Emitir cambios al padre
    useEffect(() => {
        const selectedGuide = guideOptions.find(g => g.id === guideKey) || null;
        const selectedVehicle = vehicleOptions.find(v => v.id === vehicleKey) || null;

        // Mapear selectedAttractions (keys) a objetos completos cuando existan
        const available = Array.isArray(extrasAttractions) ? extrasAttractions : [];
        const selectedAttractionsObjs = available
            .filter(a => selectedAttractions.includes(getAttractionKey(a)))
            .map(a => ({ ...a }));

        let vehicleWithOption = null;
        if (selectedVehicle) {
            vehicleWithOption = { ...selectedVehicle };
            const voVal = selectedVehicle?.vehicle_option;
            if (voVal) {
                const voList = Array.isArray(voVal) ? voVal : [voVal];
                const firstVo = voList[0] || null;
                if (firstVo) {
                    vehicleWithOption.vehicle_option = {
                        ...firstVo,
                        value: vehicleOptionChecked ? (Number(firstVo?.value ?? 1) || 1) : 0
                    };
                }
            }
        }

        onSelectionChange({
            guide: selectedGuide,
            vehicle: vehicleWithOption,
            attractions: selectedAttractionsObjs,   // objetos completos cuando es posible
            vehicleOptionChecked
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guideKey, guideOptions, vehicleKey, vehicleOptions, vehicleOptionChecked, selectedAttractions, extrasAttractions]);

    // ─────────── Inicializar attractions desde props
    // Detecta flanco de subida del modal y solo inicializa una vez por apertura
    const lastModalOpenRef = useRef(false);

    useEffect(() => {
        const justOpened = modalOpen && !lastModalOpenRef.current;
        lastModalOpenRef.current = modalOpen;

        if (!justOpened || attractionsInitRef.current) return;

        // 1) Si hay initialAttractions, úsalos
        if (Array.isArray(initialAttractions) && initialAttractions.length > 0) {
            setSelectedAttractions(initialAttractions.map(a => getAttractionKey(a)));
            attractionsInitRef.current = true;
            return;
        }

        // 2) Si NO hay initialAttractions:
        //    - Si extrasAttractions está vacío, NO limpies, deja lo que tenga el usuario
        //    - Si extrasAttractions tiene datos pero no hay selección inicial, también NO toques nada
        attractionsInitRef.current = true;
    }, [modalOpen]); // ← solo depende de modalOpen

    // Al cerrar el modal, permite re-inicializar en la próxima apertura
    useEffect(() => {
        if (!modalOpen) {
            attractionsInitRef.current = false;
        }
    }, [modalOpen]);


    // ─────────── Render
    return (
        <>
            <Typography variant="h5" sx={{ fontSize: '20px', color: 'secondary.main' }}>
                Detalles del tour
            </Typography>

            {(Number(guideIncluded) !== 0 || guideOptions.length > 0) && (
                <Box sx={{ display: 'grid', width: '100%', py: '5px' }}>
                    <CmLabel htmlFor="guideType">¿Como quieres hacer tu tour?</CmLabel>
                    <Select
                        labelId="guide-type"
                        name="guideType"
                        value={guideKey ? guideKey : (hasGuideExtras ? String(guideOptions[0]?.id ?? '') : '')}
                        onChange={handleChangeGuide}
                        disabled={!hasGuideExtras}
                        MenuProps={MenuProps}
                        sx={{ fontSize: '13px', height: '40px', borderRadius: '8px' }}
                    >
                        {guideOptions.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.type_guide} {item.price ? `- $${item.price}` : ''}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            )}

            {(vehicleOptions.length > 1 && String(vehicleOptions[0].id) === '0') && (
                <Box sx={{ display: 'grid', width: '100%', py: '5px' }}>
                    <CmLabel htmlFor="vehicle">Vehículo</CmLabel>
                    <Select
                        labelId="vehicle-id"
                        name="vehicle"
                        value={vehicleKey}
                        onChange={handleChangeVehicle}
                        disabled={!hasVehicleExtras}
                        MenuProps={MenuProps}
                        sx={{ fontSize: '13px', height: '40px', borderRadius: '8px' }}
                    >
                        {vehicleOptions.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.vehicle_type} {item.price ? `- $${item.price}` : ''}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            )}

            {(() => {
                const selectedVehicle = vehicleOptions.find(v => v.id === vehicleKey) || null;
                const baseOk = vehicleOptions.length > 1 && String(vehicleOptions[0]?.id) === '0' && selectedVehicle != null;
                if (!baseOk) return null;

                const selectedFromList = vehicleOptions.find(v => String(v.id) === String(vehicleKey)) || null;
                if (!selectedFromList || !('vehicle_option' in selectedFromList) || !selectedFromList.vehicle_option) {
                    return null;
                }

                const vo = Array.isArray(selectedFromList.vehicle_option)
                    ? selectedFromList.vehicle_option[0]
                    : selectedFromList.vehicle_option;

                if (!vo) return null;

                const label = `${vo?.name ?? 'Opción'}${vo?.price ? ` - $${vo.price}` : ''}`;

                return (
                    <Box sx={{ pb: '10px' }}>
                        <Box>
                            <CmLabel htmlFor="complements">Complementos</CmLabel>
                        </Box>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={vehicleOptionChecked}
                                    onChange={handleToggleVehicleOption}
                                />
                            }
                            label={label}
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                        />
                    </Box>
                );
            })()}

            <Box sx={{ display: 'grid', width: '100%', py: '5px' }}>
                <CmLabel htmlFor="atractions">Atracciones del tour</CmLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    {(Array.isArray(extrasAttractions) ? extrasAttractions : []).map((item) => {
                        const name = item?.name || 'Sin nombre';
                        const price = (item?.rates && item.rates[0] && item.rates[0].price) || 0;
                        const svg = item?.icon || null;
                        const itemKey = getAttractionKey(item);      // ← key normalizada
                        const checked = selectedAttractions.includes(itemKey);
                        const paxCount = Object.values(persons).reduce((s, v) => s + (Number(v) || 0), 0) || 1;

                        const toggle = () => toggleAttraction(itemKey);
                        const onKeyDown = (e) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                                e.preventDefault();
                                toggle();
                            }
                        };

                        return (
                            <Box
                                key={itemKey}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 1,
                                    borderRadius: 1,
                                    border: '1px solid #eee',
                                    cursor: 'pointer',
                                    backgroundColor: checked ? '#f5f5f5' : 'transparent',
                                    transition: 'background 0.2s',
                                }}
                                onClick={toggle}
                                role="checkbox"
                                aria-checked={checked}
                                tabIndex={0}
                                onKeyDown={onKeyDown}
                            >
                                <Box
                                    sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: 13, color: 'secondary.main' }}>{name}</Typography>
                                    <Typography sx={{ fontSize: 12, color: 'secondary.main' }}>
                                        {price ? `$${price} x ${paxCount}` : 'Gratis'}
                                    </Typography>
                                </Box>
                                <Checkbox
                                    checked={checked}
                                    onChange={(e) => { e.stopPropagation(); toggle(); }}
                                    onClick={(e) => e.stopPropagation()}
                                    inputProps={{ 'aria-label': `Seleccionar ${name}` }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </>
    );
};

export default Step2;
